import { format } from "date-fns";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { VariableDefinition, tokenizeVariableTemplate } from "./variable-field";
import { fullDateFullYearFormat, timeFormat } from "./date";
import { getReportDateRangeLabel } from "./reports";
import { getAccountingBasisLabel } from "./accountingBasis";

export type ReportVariableKey =
    | "entityName"
    | "firmName"
    | "reportTitle"
    | "reportPeriod"
    | "periodStart"
    | "periodEnd"
    | "basis"
    | "generatedDate"
    | "generatedTime";

/** Required rather than optional: a caller that misses a newly added field fails to compile instead of silently rendering it blank. */
export interface ReportVariableContext {
    reportType: AccountingReportType;
    entityName: string | null;
    firmName: string | null;
    reportTitle: string | null;
    /** When set, `reportPeriod` is "As of …" and both boundaries collapse to `periodEnd`. */
    isPointInTime: boolean;
    periodStart: Date | null;
    periodEnd: Date | null;
    basis: AccountingLedgerBasis | null;
    /** When the report spans several ledger bases, `basis` renders "Mixed" instead of claiming one. */
    isMixedBasis: boolean;
    /** `null` defers `generatedDate` / `generatedTime` to whoever renders, since only the reader's clock is local to them. */
    now: Date | null;
}

export interface ReportVariable extends VariableDefinition {
    key: ReportVariableKey;
    resolve: (context: ReportVariableContext) => string;
}

const POINT_IN_TIME_DATE_FORMAT = "MMMM d, yyyy";

export const MIXED_BASIS_LABEL = "Mixed";

function variableToken(key: string): string {
    return `{{${key}}}`;
}

function formatGeneratedDate(now: Date): string {
    return format(now, fullDateFullYearFormat);
}

function formatGeneratedTime(now: Date): string {
    return format(now, timeFormat);
}

function formatPeriodBoundary(date?: Date | null): string {
    return date ? format(date, POINT_IN_TIME_DATE_FORMAT) : "";
}

function resolveReportPeriod(context: ReportVariableContext): string {
    if (context.isPointInTime) {
        return context.periodEnd
            ? `As of ${format(context.periodEnd, POINT_IN_TIME_DATE_FORMAT)}`
            : "";
    }
    return getReportDateRangeLabel(
        context.periodStart ?? undefined,
        context.periodEnd ?? undefined,
    );
}

/** Single source of truth: the picker reads key/label/description, the interpolator reads `resolve`. */
export const REPORT_VARIABLES: ReportVariable[] = [
    {
        key: "entityName",
        label: "Entity name",
        description: "The business entity's name",
        resolve: (context) => context.entityName ?? "",
    },
    {
        key: "firmName",
        label: "Firm name",
        description: "The accounting firm's name",
        resolve: (context) => context.firmName ?? "",
    },
    {
        key: "reportTitle",
        label: "Report title",
        description: "The report's title, e.g. Profit & Loss",
        resolve: (context) =>
            context.reportTitle ??
            ACCOUNTING_REPORT_LABELS[context.reportType] ??
            "",
    },
    {
        key: "reportPeriod",
        label: "Report period",
        description: "The report's date range, or as-of date",
        resolve: resolveReportPeriod,
    },
    {
        key: "periodStart",
        label: "Period start",
        description: "The first day of the report period",
        resolve: (context) =>
            formatPeriodBoundary(
                context.isPointInTime ? context.periodEnd : context.periodStart,
            ),
    },
    {
        key: "periodEnd",
        label: "Period end",
        description: "The last day of the report period",
        resolve: (context) => formatPeriodBoundary(context.periodEnd),
    },
    {
        key: "basis",
        label: "Accounting basis",
        description: "The accounting basis, cash or accrual",
        resolve: (context) =>
            context.isMixedBasis
                ? MIXED_BASIS_LABEL
                : getAccountingBasisLabel(context.basis),
    },
    {
        key: "generatedDate",
        label: "Generated date",
        description: "The date the report was generated",
        resolve: (context) =>
            context.now === null
                ? variableToken("generatedDate")
                : formatGeneratedDate(context.now),
    },
    {
        key: "generatedTime",
        label: "Generated time",
        description: "The time the report was generated",
        resolve: (context) =>
            context.now === null
                ? variableToken("generatedTime")
                : formatGeneratedTime(context.now),
    },
];

const REPORT_VARIABLE_BY_KEY: Map<string, ReportVariable> = new Map();
for (const variable of REPORT_VARIABLES) {
    REPORT_VARIABLE_BY_KEY.set(variable.key, variable);
}

export function getReportVariable(key: string): ReportVariable | undefined {
    return REPORT_VARIABLE_BY_KEY.get(key);
}

/**
 * Unresolved keys (reachable only via registry drift) render blank. Named to
 * avoid colliding with agentChat's `interpolate` export.
 */
export function interpolateReportVariables(
    template: string,
    context: ReportVariableContext,
): string {
    return tokenizeVariableTemplate(template)
        .map((token) =>
            token.kind === "variable"
                ? (REPORT_VARIABLE_BY_KEY.get(token.key)?.resolve(context) ??
                  "")
                : token.value,
        )
        .join("");
}

/** Completes text a `now: null` context left deferred, where the reader's clock is known. */
export function resolveViewerClockVariables(text: string, now: Date): string {
    return tokenizeVariableTemplate(text)
        .map((token) => {
            if (token.kind === "text") {
                return token.value;
            }
            if (token.key === "generatedDate") {
                return formatGeneratedDate(now);
            }
            if (token.key === "generatedTime") {
                return formatGeneratedTime(now);
            }
            return variableToken(token.key);
        })
        .join("");
}
